import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { FMT } from '../../../../util/formatter-service';
import { StringFormatterId } from '../../../../util/formatters/StringFormatters';

export const KpiTypeParam = 'kpiType';
export const KpiType = {
  Quantitative: 'quantitative',
  Qualitative: 'qualitative',
} as const;
export type KpiType = (typeof KpiType)[keyof typeof KpiType];
const allowedKpiTypes = new Set([...Object.values(KpiType)]);

export function useFinancialsType() {
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (allowedKpiTypes.has(params.get(KpiTypeParam) as KpiType)) {
      return;
    }
    setParams({ [KpiTypeParam]: KpiType.Quantitative }, { replace: true });
  }, [params, setParams]);

  const setSelectedKpiType = useCallback(
    (kpiType: KpiType) => {
      setParams({ [KpiTypeParam]: kpiType });
    },
    [setParams]
  );

  const selectedKpiType: KpiType = (params.get(KpiTypeParam) ?? KpiType.Quantitative) as KpiType;

  return {
    selectedKpiType,
    setSelectedKpiType,
  };
}

export function FinancialsTypeToggle() {
  const { selectedKpiType, setSelectedKpiType } = useFinancialsType();
  return (
    <ToggleButtonGroup
      color='secondary'
      value={selectedKpiType}
      exclusive
      onChange={(e, newValue) => {
        if (!newValue) {
          return;
        }
        setSelectedKpiType(newValue);
      }}
      aria-label='Select KPI type'
    >
      <ToggleButton value={KpiType.Quantitative}>
        {FMT.format(StringFormatterId.capitalizedString, KpiType.Quantitative)}
      </ToggleButton>
      <ToggleButton value={KpiType.Qualitative}>
        {FMT.format(StringFormatterId.capitalizedString, KpiType.Qualitative)}
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
